import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { COMPANY_SETTINGS_TAG_TYPE, USER_SETTINGS_TAG_TYPE } from './constants';
import type { UserSetting, UserSettingsResponse, CreateSettingParams, UpdateSettingParams } from './types';

export const settingsApi = createApi({
  reducerPath: 'settingsApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [COMPANY_SETTINGS_TAG_TYPE, USER_SETTINGS_TAG_TYPE],
  endpoints: (builder) => ({
    getUserSettings: builder.query<UserSettingsResponse, void>({
      query: () => 'v1/accounts/user-settings/',
      providesTags: () => [
        {
          type: USER_SETTINGS_TAG_TYPE,
          id: 'USER_SETTINGS',
        },
      ],
    }),
    createUserSettings: builder.mutation<UserSetting, CreateSettingParams>({
      query: (data) => ({
        url: 'v1/accounts/user-settings/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        {
          type: USER_SETTINGS_TAG_TYPE,
          id: 'USER_SETTINGS',
        },
      ],
    }),
    updateUserSettings: builder.mutation<UserSetting, UpdateSettingParams>({
      query: ({ data, settingsCode }) => ({
        url: `v1/accounts/user-settings/${settingsCode}/`,
        method: 'PATCH',
        body: data,
      }),
      async onQueryStarted(
        { data, settingsCode },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          settingsApi.util.updateQueryData(
            'getUserSettings',
            undefined,
            (draft) => {
              const settings = draft?.results ?? draft;
              if (!Array.isArray(settings)) return;
              const existing = settings.find(
                (s: any) => s.code === settingsCode
              );
              if (existing) {
                existing.value = data.value;
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: [
        {
          type: USER_SETTINGS_TAG_TYPE,
          id: 'USER_SETTINGS',
        },
      ],
    }),
    deleteUserSettings: builder.mutation<void, string>({
      query: (settingsCode) => ({
        url: `v1/accounts/user-settings/${settingsCode}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        {
          type: USER_SETTINGS_TAG_TYPE,
          id: 'USER_SETTINGS',
        },
      ],
    }),
    getCompanySettings: builder.query<UserSettingsResponse, void>({
      query: () => `v1/accounts/company-settings/`,
      providesTags: () => [
        {
          type: COMPANY_SETTINGS_TAG_TYPE,
          id: 'COMPANY_SETTINGS',
        },
      ],
    }),
    createCompanySettings: builder.mutation<UserSetting, CreateSettingParams>({
      query: (data) => ({
        url: `v1/accounts/company-settings/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        {
          type: COMPANY_SETTINGS_TAG_TYPE,
          id: 'COMPANY_SETTINGS',
        },
      ],
    }),
    updateCompanySettings: builder.mutation<UserSetting, UpdateSettingParams>({
      query: ({ data, settingsCode }) => ({
        url: `v1/accounts/company-settings/${settingsCode}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [
        {
          type: COMPANY_SETTINGS_TAG_TYPE,
          id: 'COMPANY_SETTINGS',
        },
      ],
    }),
    deleteCompanySettings: builder.mutation<void, string>({
      query: (settingsCode) => ({
        url: `v1/accounts/company-settings/${settingsCode}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        {
          type: COMPANY_SETTINGS_TAG_TYPE,
          id: 'COMPANY_SETTINGS',
        },
      ],
    }),
  }),
});

export const {
  useGetUserSettingsQuery,
  useCreateUserSettingsMutation,
  useUpdateUserSettingsMutation,
  useDeleteUserSettingsMutation,
  useGetCompanySettingsQuery,
  useUpdateCompanySettingsMutation,
  useCreateCompanySettingsMutation,
  useDeleteCompanySettingsMutation,
} = settingsApi;
